import React, { useState } from 'react';
import { Award, ShieldCheck, Clock } from 'lucide-react';
import CertificateLightbox from './CertificateLightbox';

/**
 * CorporateCertificates — canonical trust surface.
 *
 * Renders the two states of the canonical certificate SSOT
 * (`treatx_corporate_certificates`) as two visually distinct rows:
 *
 *   1. "Certified & Authorized"        → status === "active"
 *   2. "Authorizations in Progress"    → status === "application_in_progress"
 *
 * Rule (per Owner directive, Slice 2):
 *   A user scanning the page MUST NOT reasonably interpret a pending
 *   authorization as already granted. The two lists therefore live in
 *   separate rows under separate headings — not the same row with a
 *   small badge overlay.
 *
 * Props:
 *   certificates    — array from GET /api/public/corporate-certificates
 *   labels          — { grantedHeading, pendingHeading, pendingCaption,
 *                       grantedCaption, pending, clickToEnlarge, lightbox: {…} }
 *   variant         — "homepage" | "about"  (about renders larger tiles)
 */
const CorporateCertificates = ({ certificates = [], labels = {}, variant = 'homepage',
                                 requireEvidence = false }) => {
  const [lightboxCert, setLightboxCert] = useState(null);

  if (!Array.isArray(certificates) || certificates.length === 0) return null;

  // TREATX-CERT-EVIDENCE-01 — a record marked `active` is a CLAIM, not proof.
  // `requireEvidence` (used by the Landing V3 candidate) shows a granted badge
  // only when the record carries evidence: a certificate number, a validity
  // date, or a stored certificate asset. Placeholder asset URLs never count,
  // on any surface. Nothing is fabricated and no record is modified.
  const isPlaceholderUrl = (url) => typeof url === 'string'
    && /(^|\/\/|\.)(example\.(com|org|net|invalid)|localhost)(\/|:|$)/i.test(url);
  const hasEvidence = (c) => Boolean(
    c.certificate_number || c.valid_until
    || (c.asset_image_url && !isPlaceholderUrl(c.asset_image_url)),
  );

  const granted = certificates
    .filter((c) => c.status === 'active')
    .filter((c) => (requireEvidence ? hasEvidence(c) : true))
    .map((c) => (isPlaceholderUrl(c.asset_image_url)
      ? { ...c, asset_image_url: null }
      : c))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const pending = certificates
    .filter((c) => c.status === 'application_in_progress')
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  if (granted.length === 0 && pending.length === 0) return null;

  // TREATX-CERT-EVIDENCE-02 — on a marketing surface (`requireEvidence`),
  // applications that are merely in progress are not proof of anything, and a
  // band of empty dashed tiles reads as an unfinished page. With no
  // evidence-backed certificate to show, the whole band is withheld. The About
  // page keeps the full, honest picture because it renders without
  // `requireEvidence`.
  if (requireEvidence && granted.length === 0) return null;

  const openLightbox = (cert) => setLightboxCert(cert);
  const closeLightbox = () => setLightboxCert(null);

  const tileClasses = variant === 'about'
    ? 'p-5 min-h-[140px]'
    : 'p-4 min-h-[120px]';

  return (
    <>
      {granted.length > 0 && (
        <div className="mb-8" data-testid="corp-cert-granted-section">
          <div className="text-center mb-4">
            <h3
              className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center justify-center gap-2"
              data-testid="corp-cert-granted-heading"
            >
              <ShieldCheck className="h-4 w-4 text-emerald-600" aria-hidden />
              {labels.grantedHeading || 'Certified & Authorized'}
            </h3>
            {labels.grantedCaption && (
              <p className="text-xs text-slate-500 mt-1">{labels.grantedCaption}</p>
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4" data-testid="corp-cert-granted-grid">
            {granted.map((cert) => {
              return (
                <button
                  key={cert.certificate_id}
                  type="button"
                  onClick={() => openLightbox(cert)}
                  className={`group relative flex w-[calc(50%-0.5rem)] sm:w-44 md:w-48 flex-col items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white ${tileClasses} transition-all hover:border-emerald-300 hover:shadow-md cursor-pointer`}
                  aria-label={cert.public_title}
                  data-testid={`corp-cert-granted-tile-${cert.certificate_id}`}
                >
                  {cert.asset_image_url ? (
                    <img
                      src={cert.asset_image_url}
                      alt={cert.public_title}
                      className="h-12 w-auto max-w-full object-contain"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center">
                      <Award className="h-5 w-5 text-emerald-600" aria-hidden />
                    </div>
                  )}
                  <span className="text-xs sm:text-sm font-medium text-slate-800 text-center leading-tight">
                    {cert.public_title}
                  </span>
                  {cert.issuing_authority && (
                    <span className="text-[10px] text-slate-500 text-center leading-tight line-clamp-1">
                      {cert.issuing_authority}
                    </span>
                  )}
                  {labels.clickToEnlarge && (
                    <span className="sr-only">{labels.clickToEnlarge}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-2" data-testid="corp-cert-pending-section">
          <div className="text-center mb-4">
            <h3
              className="text-sm font-semibold text-amber-800 uppercase tracking-wide flex items-center justify-center gap-2"
              data-testid="corp-cert-pending-heading"
            >
              <Clock className="h-4 w-4 text-amber-700" aria-hidden />
              {labels.pendingHeading || 'Authorizations in Progress'}
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              {labels.pendingCaption
                || 'These authorizations have been applied for and are not yet granted.'}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4" data-testid="corp-cert-pending-grid">
            {pending.map((cert) => {
              return (
                <button
                  key={cert.certificate_id}
                  type="button"
                  onClick={() => openLightbox(cert)}
                  className={`group relative flex w-[calc(50%-0.5rem)] sm:w-44 md:w-48 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/60 ${tileClasses} transition-all hover:border-amber-400 hover:bg-amber-50 cursor-pointer`}
                  aria-label={`${cert.public_title} — ${labels.pending || 'Application in progress'}`}
                  data-testid={`corp-cert-pending-tile-${cert.certificate_id}`}
                >
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    data-testid={`corp-cert-pending-label-${cert.certificate_id}`}
                  >
                    <Clock className="h-3 w-3" aria-hidden />
                    {labels.pending || 'Application in progress'}
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-slate-800 text-center leading-tight">
                    {cert.public_title}
                  </span>
                  {cert.issuing_authority && (
                    <span className="text-[10px] text-slate-600 text-center leading-tight line-clamp-1">
                      {cert.issuing_authority}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <CertificateLightbox
        open={!!lightboxCert}
        onClose={closeLightbox}
        certificate={lightboxCert}
        labels={labels.lightbox || {}}
      />
    </>
  );
};

export default CorporateCertificates;
